import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  Grid,
  MenuItem,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { StyledTrasladosDialog } from '../../../../styled/traslados';
import { getArtPrestadores } from '../../../../../actions/arts';
import { getFormModel } from '../../../../../actions/forms';
import {
  autorizarTraslado,
  cancelarTramo,
  cancelarTrasladoCompleto,
  getMotivosAnulacionTraslado,
  getTrasladosPendientes,
  resolverNegativoTramo,
  rechazarTramo,
  getObservacionesPT,
  fetchPrestadoresTraslado,
  fetchImagenesNegativo
} from '../../../../../actions/traslados';
import CustomDialog from '../../../../commons/dialog/customDialog';
import snackbarUtils from '../../../../../utils/snackbarUtils';
import FormikSelect from '../../../../commons/formik/formikSelect';
import FormikTextField from '../../../../commons/formik/formikTextField';
import {
  catalogOptionLabel,
  getFormFieldOptions,
} from '../../../forms/fptTraslados/fptTrasladosUtils';
import AgenciasAsignacionMap from "./AgenciasAsignacionMap/AgenciasAsignacionMap";

const unwrapPromiseActionData = (action) => {
  const payload = action?.value?.data ?? action?.value ?? action?.data;
  return Array.isArray(payload) ? payload : [];
};

const normalizeObservacionPt = (obs) => ({
  usuarioCreacion: obs?.usuarioCreacion ?? obs?.UsuarioCreacion ?? '',
  fechaHoraCreacion: obs?.fechaHoraCreacion ?? obs?.FechaHoraCreacion,
  observacion: obs?.observacion ?? obs?.Observacion ?? '',
});

const isValidTramoAgencia = (value, agencias, tramoIdAgencia) => {
  if (value == null || String(value).trim() === '') {
    return false;
  }
  const id = Number.parseInt(String(value), 10);
  if (!Number.isFinite(id) || id <= 0) {
    return false;
  }
  if (agencias.some((agencia) => String(agencia.id) === String(id))) {
    return true;
  }
  return tramoIdAgencia != null && String(tramoIdAgencia) === String(id);
};

const AuditarTrasladoDialog = ({ open, onClose, onComplete, onAuditarTraslado, data, rol = 'gs' }) => {
  const isAT = rol === 'at';
  const isGS = rol === 'gs';
  const dispatch = useDispatch();
  const formModel = useSelector((state) => state.forms.model);
  const artPrestadores = useSelector((state) => state.arts.artPrestadores);
  const associatedArtId = useSelector((state) =>
    state.auth.artAsociada?.id ||
    state.formsState.commonProperties?.idArt ||
    artPrestadores?.data?.[0]?.arts?.[0]?.id ||
    null
  );
  const trasladoArtId = Number(data?.idArt);
  const idArt = trasladoArtId > 0 ? trasladoArtId : associatedArtId;
  const motivosAnulacion = useSelector((state) => state.traslados.motivosAnulacion);
  const agencias = useMemo(
    () => getFormFieldOptions(formModel?.data?.fields, "idAgencia", "IdAgencia"),
    [formModel?.data?.fields],
  );
  const motivosTraslado = useMemo(
    () => getFormFieldOptions(formModel?.data?.fields, "idMotivoTraslado", "IdMotivoTraslado"),
    [formModel?.data?.fields],
  );
  const showMapAuditoria = formModel?.data?.configuration?.showMapAuditoria ?? true;
  const mapOrigen = useMemo(
    () => ({ lat: data?.latOrigen, lng: data?.lngOrigen }),
    [data?.latOrigen, data?.lngOrigen],
  );
  const mapDestino = useMemo(
    () => ({ lat: data?.latDestino, lng: data?.lngDestino }),
    [data?.latDestino, data?.lngDestino],
  );
  const selectAgenciaRef = useRef(() => { });
  const handleMapSelectAgencia = useCallback((id) => {
    selectAgenciaRef.current(id);
  }, []);
  const catalogsLoading = Boolean(formModel?.isFetching || artPrestadores?.isFetching);

  const [showCancelForm, setShowCancelForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [observacionesPT, setObservacionesPT] = useState([]);
  const [prestadoresTraslado, setPrestadoresTraslado] = useState([]);
  const [prestadoresLoading, setPrestadoresLoading] = useState(false);
  const [negativeImages, setNegativeImages] = useState([]);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const actionRef = useRef(null);

  useEffect(() => {
    if (open) {
      setShowCancelForm(false);
      const trasladoArtIdForMotivos = Number(data?.idArt);
      const motivosArtId = trasladoArtIdForMotivos > 0 ? trasladoArtIdForMotivos : associatedArtId;
      dispatch(getMotivosAnulacionTraslado(motivosArtId));
      if (data?.idTramo) {
        const trasladoArtId = Number(data?.idArt);
        const observacionesArtId = trasladoArtId > 0 ? trasladoArtId : associatedArtId;
        dispatch(getObservacionesPT(data.idTramo, observacionesArtId))
          .then((action) => setObservacionesPT(unwrapPromiseActionData(action).map(normalizeObservacionPt)))
          .catch(() => setObservacionesPT([]));
      }
      if (isAT) {
        setPrestadoresLoading(true);
        dispatch(fetchPrestadoresTraslado())
          .then(res => setPrestadoresTraslado(res || []))
          .catch(() => setPrestadoresTraslado([]))
          .finally(() => setPrestadoresLoading(false));
      }
      if (data?.posibleNegativo && data?.idTramo) {
        dispatch(fetchImagenesNegativo(data.idTramo))
          .then(res => setNegativeImages(res || []))
          .catch(() => setNegativeImages([]));
      } else {
        setNegativeImages([]);
      }
    }
  }, [open, data, isAT, associatedArtId, dispatch]);

  useEffect(() => {
    if (!open) return;
    if (!artPrestadores?.data?.length && !artPrestadores?.isFetching) {
      dispatch(getArtPrestadores());
    }
  }, [open, dispatch, artPrestadores?.data?.length, artPrestadores?.isFetching]);

  useEffect(() => {
    if (!open || !idArt) return;
    const siniestroParsed = parseInt(String(data?.siniestro).trim(), 10);
    const safeSiniestro = isNaN(siniestroParsed) ? 0 : siniestroParsed;

    dispatch(getFormModel("fpt", idArt, safeSiniestro));
  }, [open, idArt, data?.siniestro, dispatch]);

  const validationSchema = useMemo(() => {
    const requiresAgencia = !showCancelForm && !data?.posibleNegativo;
    const tramoIdAgencia = data?.idAgencia;

    return Yup.object().shape({
      idAgencia: requiresAgencia
        ? Yup.string()
          .trim()
          .required('Debe asignar una agencia')
          .test(
            'agencia-valida',
            'Debe seleccionar una agencia válida',
            (value) => isValidTramoAgencia(value, agencias, tramoIdAgencia),
          )
        : Yup.string(),
      idMotivoTraslado: isAT && !showCancelForm && !data?.posibleNegativo
        ? Yup.string()
          .required('Debe seleccionar un motivo de traslado')
          .test('not-zero-motivo', 'Debe seleccionar un motivo de traslado válido', (val) => val && val !== '0' && val !== 0)
        : Yup.string(),
      idMotivo: showCancelForm
        ? Yup.string().required('El motivo es obligatorio')
        : Yup.string(),
      observaciones: showCancelForm
        ? Yup.string().required('Las observaciones son obligatorias')
        : Yup.string(),
      resolucionNegativo: data?.posibleNegativo && !showCancelForm
        ? Yup.string().required('Debe seleccionar una resolucion')
        : Yup.string(),
    });
  }, [isAT, showCancelForm, data?.posibleNegativo, data?.idAgencia, agencias]);

  if (!data) return null;

  const alarmasFiltered = Array.isArray(data.alarmas) ? data.alarmas : [];
  const estadoActualLabel = data.estado || (isGS ? 'Pendiente Autorización' : '');

  const handleRefresh = () => {
    if (onComplete) {
      onComplete();
      return;
    }
    dispatch(getTrasladosPendientes(idArt));
    onClose();
  };

  const handleSubmit = (values) => {
    const actionType = actionRef.current;
    if (!actionType) return;

    setLoading(true);

    const handleSuccess = (msg) => {
      snackbarUtils.success(msg);
      handleRefresh();
    };

    const handleError = () => {
      snackbarUtils.error("Ocurrió un error al procesar la operación.");
    };

    if (actionType === 'AUTORIZAR') {
      const idAgencia = values.idAgencia ? parseInt(values.idAgencia, 10) : null;
      const idMotivoTraslado = values.idMotivoTraslado ? parseInt(values.idMotivoTraslado, 10) : null;
      dispatch(autorizarTraslado(data.idTramo, idAgencia, !isAT, idArt, idMotivoTraslado))
        .then(() => handleSuccess(isAT ? "Tramo auditado correctamente." : "Tramo autorizado correctamente."))
        .catch(handleError)
        .finally(() => setLoading(false));
    } else if (actionType === 'CANCELAR') {
      if (isAT) {
        dispatch(cancelarTramo(data.idTramo, parseInt(values.idMotivo, 10), values.observaciones, idArt))
          .then(() => handleSuccess("Tramo cancelado correctamente."))
          .catch(handleError)
          .finally(() => setLoading(false));
      } else {
        dispatch(rechazarTramo(data.idTramo, parseInt(values.idMotivo, 10), values.observaciones, idArt))
          .then(() => handleSuccess("Tramo rechazado correctamente."))
          .catch(handleError)
          .finally(() => setLoading(false));
      }
    } else if (actionType === 'CANCELAR_TRASLADO') {
      dispatch(cancelarTrasladoCompleto(data.idTraslado, parseInt(values.idMotivo, 10), values.observaciones, idArt))
        .then(() => handleSuccess("Traslado cancelado completamente."))
        .catch(handleError)
        .finally(() => setLoading(false));
    } else if (actionType === 'RESOLVER_NEGATIVO') {
      dispatch(resolverNegativoTramo(data.idTramo, data.idArt || idArt, values.resolucionNegativo))
        .then(() => handleSuccess("Negativo resuelto correctamente."))
        .catch(handleError)
        .finally(() => setLoading(false));
    }
  };

  return (
    <Dialog disableEnforceFocus disableScrollLock open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledTrasladosDialog.Header>
        AUDITANDO TRAMO {data.idTramo} - TRASLADO {data.idTraslado}
        <StyledTrasladosDialog.CloseButton
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </StyledTrasladosDialog.CloseButton>
      </StyledTrasladosDialog.Header>

      <Formik
        initialValues={{
          idAgencia: data?.idAgencia != null && data.idAgencia !== 0 ? String(data.idAgencia) : '',
          idMotivoTraslado: data?.idMotivoTraslado != null && data.idMotivoTraslado !== 0 ? String(data.idMotivoTraslado) : '',
          idPrestadorTraslado: (() => {
            if (data?.idPrestador) return String(data.idPrestador);
            const found = agencias.find(a => String(a.id) === String(data?.idAgencia));
            return found?.idPrestador ? String(found.idPrestador) : '';
          })(),
          agenciaNombre: data?.agencia || '',
          estadoActualView: estadoActualLabel,
          idMotivo: '',
          observaciones: '',
          resolucionNegativo: '',
          horaLlegadaView: data?.horaLlegada || data?.horaLlegadaNeg || '',
          informadoPorView: data?.agencia || data?.informadoPor || '',
          motivoNegativoView: data?.motivoNegativo || data?.motivoNeg || '',
          observacionesNegativoView: data?.observacionesNegativo || data?.observNeg || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm, values, setFieldValue, errors, touched, setFieldTouched }) => {
          const idAgenciaError = touched.idAgencia && errors.idAgencia ? errors.idAgencia : null;
          selectAgenciaRef.current = (id) => {
            const match = agencias.find((agencia) => String(agencia.id) === String(id));
            setFieldValue('idAgencia', match ? match.id : id);
            if (match && match.idPrestador) {
              setFieldValue('idPrestadorTraslado', String(match.idPrestador));
            }
          };

          const filteredAgencias = !values.idPrestadorTraslado
            ? agencias
            : agencias.filter(a => String(a.idPrestador) === String(values.idPrestadorTraslado));

          return (
            <Form>
              <DialogContent sx={{ mt: 2, padding: '24px 28px 10px' }}>
                {showCancelForm ? (
                  <Box>
                    <Typography variant="h6" sx={{ color: '#d94040', fontSize: '18px', mb: 2 }}>
                      {isAT ? "Cancelar" : "Rechazar"} Tramo {data.idTramo}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      {isAT ? "El tramo pasara a Cancelado. Los demas tramos no se veran afectados." : "El tramo volvera a la bandeja del Agente para revalidacion y nueva asignacion de agencia."}
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormikSelect name="idMotivo" label="Motivo *" loading={motivosAnulacion?.isFetching} variant="outlined">
                          <MenuItem value="">Seleccionar motivo...</MenuItem>
                          {motivosAnulacion?.data?.map((m) => (
                            <MenuItem key={m.idMotivo} value={m.idMotivo}>{m.descripcion}</MenuItem>
                          ))}
                        </FormikSelect>
                      </Grid>
                      <Grid item xs={12}>
                        <FormikTextField
                          name="observaciones"
                          label="Observaciones *"
                          fullWidth
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <>
                    {alarmasFiltered.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Advertencias del tramo:</Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {alarmasFiltered.map((al, idx) => (
                            <li key={idx}><Typography variant="body2">{al}</Typography></li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                    <StyledTrasladosDialog.SectionTitle variant="h6">
                      Datos del traslado
                    </StyledTrasladosDialog.SectionTitle>
                    <StyledTrasladosDialog.SectionDivider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <StyledTrasladosDialog.FieldLabel variant="body2">Trabajador</StyledTrasladosDialog.FieldLabel>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Typography variant="body2">{data.trabajador} &middot; DNI {data.dni}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <StyledTrasladosDialog.FieldLabel variant="body2">Siniestro</StyledTrasladosDialog.FieldLabel>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Typography variant="body2">{data.siniestro} &middot; Gestor {data.gestor}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <StyledTrasladosDialog.FieldLabel variant="body2">Fecha y hora</StyledTrasladosDialog.FieldLabel>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Typography variant="body2">
                          {data.fechaTraslado ? moment(data.fechaTraslado).format('DD/MM/YYYY') : ''} {data.horaTraslado}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <StyledTrasladosDialog.FieldLabel variant="body2">Recorrido</StyledTrasladosDialog.FieldLabel>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Typography variant="body2">{data.origen} &rarr; {data.destino}</Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                      {isAT && !data.posibleNegativo ? (
                        <>
                          <FormikSelect
                            name="idPrestadorTraslado"
                            label="Prestador de traslado"
                            loading={prestadoresLoading}
                            variant="outlined"
                            onChange={(e) => {
                              const newPrestadorId = e.target.value;
                              setFieldValue('idPrestadorTraslado', newPrestadorId);
                              if (newPrestadorId && values.idAgencia) {
                                const currentAg = agencias.find(a => String(a.id) === String(values.idAgencia));
                                if (currentAg && String(currentAg.idPrestador) !== String(newPrestadorId)) {
                                  setFieldValue('idAgencia', '');
                                }
                              }
                            }}
                          >
                            <MenuItem value="">Todos los prestadores</MenuItem>
                            {prestadoresTraslado.map((p) => (
                              <MenuItem key={p.idPrestador} value={String(p.idPrestador)}>
                                {p.razonSocial}
                              </MenuItem>
                            ))}
                          </FormikSelect>

                          <FormikSelect
                            name="idAgencia"
                            label="Agencia *"
                            loading={catalogsLoading}
                            variant="outlined"
                            onChange={(e) => {
                              const newAgenciaId = e.target.value;
                              setFieldValue('idAgencia', newAgenciaId);
                              const matchedAgencia = agencias.find(a => String(a.id) === String(newAgenciaId));
                              if (matchedAgencia && matchedAgencia.idPrestador) {
                                setFieldValue('idPrestadorTraslado', String(matchedAgencia.idPrestador));
                              }
                            }}
                          >
                            <MenuItem value="">Sin asignar</MenuItem>
                            {filteredAgencias
                              .filter((a) => String(a.id) !== '0')
                              .map((a) => (
                                <MenuItem key={a.id} value={a.id}>{catalogOptionLabel(a)}</MenuItem>
                              ))}
                          </FormikSelect>

                          <FormikSelect
                            name="idMotivoTraslado"
                            label="Motivo del traslado *"
                            loading={catalogsLoading}
                            variant="outlined"
                          >
                            <MenuItem value="">A definir</MenuItem>
                            {motivosTraslado
                              .filter((m) => String(m.id) !== '0')
                              .map((m) => (
                                <MenuItem key={m.id} value={String(m.id)}>{catalogOptionLabel(m)}</MenuItem>
                              ))}
                          </FormikSelect>
                        </>
                      ) : (
                        <FormikTextField
                          name="agenciaNombre"
                          label="Agencia *"
                          disabled
                          fullWidth
                          error={Boolean(idAgenciaError)}
                          helperText={
                            idAgenciaError || 'Asignada por el Agente de Traslados.'
                          }
                        />
                      )}

                      <FormikTextField
                        name="estadoActualView"
                        label="Estado actual"
                        disabled
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Box>

                    {isAT && !data.posibleNegativo && showMapAuditoria && (
                      <AgenciasAsignacionMap
                        agencias={agencias}
                        idArt={idArt}
                        destino={mapDestino}
                        destinoLabel={data.destino}
                        interactive={true}
                        onSelectAgencia={handleMapSelectAgencia}
                        origen={mapOrigen}
                        origenLabel={data.origen}
                        selectedAgenciaId={values.idAgencia || data.idAgencia}
                      />
                    )}
                    {observacionesPT && observacionesPT.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ color: '#5b9bf5', fontSize: '16px', mb: 1 }}>
                          Observaciones del Prestador
                        </Typography>
                        <Divider sx={{ mb: 2, backgroundColor: '#5b9bf5', height: '1px' }} />
                        {observacionesPT.map((obs, idx) => (
                          <Box key={idx} sx={{ backgroundColor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {obs.usuarioCreacion} - {moment(obs.fechaHoraCreacion).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                            <Typography variant="body2">{obs.observacion}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {data.posibleNegativo && (
                      <>
                        <Typography variant="h6" sx={{ color: '#5b9bf5', fontSize: '18px', mt: 4, mb: 0 }}>
                          Posible Negativo - informado por la agencia
                        </Typography>
                        <Divider sx={{ mb: 2, backgroundColor: '#5b9bf5', height: '2px' }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormikTextField name="horaLlegadaView" label="Hora de llegada" disabled fullWidth />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormikTextField name="informadoPorView" label="Informado por" disabled fullWidth />
                          </Grid>
                          <Grid item xs={12}>
                            <FormikTextField name="motivoNegativoView" label="Motivo" disabled fullWidth />
                          </Grid>
                          <Grid item xs={12}>
                            <FormikTextField name="observacionesNegativoView" label="Observaciones de la agencia" disabled fullWidth multiline rows={2} />
                          </Grid>
                          {negativeImages && negativeImages.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Imágenes adjuntas por la agencia:</Typography>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {negativeImages.map((img, idx) => {
                                  const src = img.base64 ? `data:${img.mimeType || 'image/png'};base64,${img.base64}` : img.url;
                                  return (
                                    <Box
                                      key={idx}
                                      sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        p: 0.5,
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        '&:hover': { borderColor: '#4a90e8', boxShadow: 1 }
                                      }}
                                      onClick={() => setSelectedImagePreview(src)}
                                    >
                                      {src ? (
                                        <img src={src} alt={img.nombre || `Adjunto ${idx}`} style={{ maxHeight: 120, maxWidth: 200, objectFit: 'contain', display: 'block' }} />
                                      ) : (
                                        <Typography variant="caption" sx={{ color: '#888', p: 2, display: 'block' }}>{img.nombre}</Typography>
                                      )}
                                      <Typography variant="caption" sx={{ display: 'block', maxWidth: 180, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', mt: 0.5 }}>
                                        {img.nombre}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <FormikSelect name="resolucionNegativo" label="Resolucion *">
                              <MenuItem value="">Seleccionar...</MenuItem>
                              <MenuItem value="conf">Confirmar el negativo - pasa a estado Negativo</MenuItem>
                              <MenuItem value="des">Desestimar - sigue en Despachado</MenuItem>
                            </FormikSelect>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </DialogContent>

              <StyledTrasladosDialog.Actions>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={showCancelForm ? () => setShowCancelForm(false) : onClose} color="primary" disabled={loading}>
                    {showCancelForm ? "VOLVER" : "CERRAR"}
                  </Button>

                  {loading ? (
                    <CircularProgress size={24} />
                  ) : showCancelForm ? (
                    <>
                      <StyledTrasladosDialog.DangerButton
                        variant="contained"
                        onClick={() => {
                          actionRef.current = 'CANCELAR';
                          submitForm();
                        }}
                      >
                        {isAT ? 'CANCELAR TRAMO' : 'RECHAZAR'}
                      </StyledTrasladosDialog.DangerButton>
                      {isAT && (
                        <StyledTrasladosDialog.DarkRedButton
                          variant="contained"
                          onClick={() => {
                            actionRef.current = 'CANCELAR_TRASLADO';
                            submitForm();
                          }}
                        >
                          CANCELAR TRASLADO
                        </StyledTrasladosDialog.DarkRedButton>
                      )}
                    </>
                  ) : data.posibleNegativo ? (
                    <StyledTrasladosDialog.PrimaryButton
                      variant="contained"
                      onClick={() => {
                        actionRef.current = 'RESOLVER_NEGATIVO';
                        submitForm();
                      }}
                    >
                      RESOLVER
                    </StyledTrasladosDialog.PrimaryButton>
                  ) : (
                    <>
                      {isAT && onAuditarTraslado && (
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            onClose();
                            onAuditarTraslado(data);
                          }}
                        >
                          EDITAR TRASLADO COMPLETO
                        </Button>
                      )}
                      {(!isAT || data.estado === 'Rechazado por Agencia') && (
                        <Button onClick={() => setShowCancelForm(true)} color="error">{isAT ? "CANCELAR TRAMO / TRASLADO" : "RECHAZAR"}</Button>
                      )}
                      <StyledTrasladosDialog.PrimaryButton
                        variant="contained"
                        onClick={() => {
                          actionRef.current = 'AUTORIZAR';
                          setFieldTouched('idAgencia', true, false);
                          submitForm();
                        }}
                      >
                        {isAT ? "AUDITAR" : "AUTORIZAR"}
                      </StyledTrasladosDialog.PrimaryButton>
                    </>
                  )}
                </Box>
              </StyledTrasladosDialog.Actions>
            </Form>
          );
        }}
      </Formik>
      {selectedImagePreview && (
        <CustomDialog
          open={Boolean(selectedImagePreview)}
          onClose={() => setSelectedImagePreview(null)}
          maxWidth="lg"
          dismissible
        >
          <StyledTrasladosDialog.Header>
            VISTA PREVIA DE IMAGEN
            <StyledTrasladosDialog.CloseButton
              aria-label="close"
              onClick={() => setSelectedImagePreview(null)}
            >
              <CloseIcon />
            </StyledTrasladosDialog.CloseButton>
          </StyledTrasladosDialog.Header>
          <DialogContent sx={{ textAlign: 'center', p: 2 }}>
            <img
              src={selectedImagePreview}
              alt="Vista previa"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </DialogContent>
        </CustomDialog>
      )}
    </Dialog>
  );
};

export default AuditarTrasladoDialog;
